import React, {
    ReactElement,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import styles from './Courses.module.scss'
import { CourseCard } from '@/components/Courses/CourseCard/CourseCard'
import {
    CourseInfo,
    Instructor,
    ReleaseStatus,
} from 'common/src/api/core/courses/types'
import { InstructorModal } from '@/components/Courses/InstructorModal/InstructorModal'
import { useRouter } from 'next/router'
import {
    UserInfoResponse,
    UserPaymentHistoryResponse,
} from 'common/src/api/websiteFrontendVsWebsiteBackend/accountMaintenance/responses'
import {
    AuthenticatorRoutingProps,
    AuthenticatorTabName,
} from '@/components/static/Authenticator/Authenticator.types'
import Modal from 'antd/lib/modal/Modal'
import authenticatorStyles from '@/components/static/Authenticator/Authenticator.module.scss'
import { Authenticator } from '@/components/static/Authenticator/Authenticator'
import { useRefResizeEffect } from '@/hooks/static/useRefResizeEffect'
import Select, { DefaultOptionType } from 'antd/lib/select'
import { PassGuaranteeComponent } from '@/components/Courses/PassGuaranteeComponent/PassGuaranteeComponent'
import { COURSE_NAME_TO_UI_LAYOUT_CONSTANT } from '@/components/Courses/courseNameToUiLayoutConstant'
import { buildFrontendLogger } from '@/logging/FrontendLogger'
import { CourseConfigType } from 'common/src/courseConfig'
import { groupCoursesByOffering } from '@/utils/courseOfferings'

const logger = buildFrontendLogger('CoursePage')

export const CoursesPage: React.FC<
    | {
          coursesInfo: CourseInfo[]
          isLoggedIn: true
          userPaymentHistory: UserPaymentHistoryResponse
          demoHistory: { courseName: string; isActive: boolean }[]
          setUserInfo: (userInfo: UserInfoResponse) => void
          courseName: string | null
          courseConfigType: CourseConfigType
      }
    | {
          coursesInfo: CourseInfo[]
          isLoggedIn: false
          userPaymentHistory: null
          demoHistory: null
          setUserInfo: null
          courseName: string | null
          courseConfigType: CourseConfigType
      }
> = (props): ReactElement => {
    const router = useRouter()
    const [activeInstructor, setActiveInstructor] = useState<Instructor>(null)

    const [isLoginModalVisible, setIsLoginModalVisible] =
        useState<boolean>(false)
    const [loginModalRoutingPurpose, setLoginModalRoutingPurpose] =
        useState<AuthenticatorRoutingProps>(null)
    const courseCard = useMemo((): ReactElement => {
        if (props.courseName) {
            const courseInfo = props.coursesInfo.find(
                (courseInfo) => courseInfo.name === props.courseName
            )
            return (
                <CourseCard
                    courseInfo={courseInfo}
                    setActiveInstructor={setActiveInstructor}
                    userPaymentHistory={props.userPaymentHistory}
                    demoHistory={props.demoHistory}
                    setLoginModalRoutingPurpose={setLoginModalRoutingPurpose}
                    setIsLoginModalVisible={setIsLoginModalVisible}
                    isLoggedIn={props.isLoggedIn}
                    setUserInfo={props.setUserInfo}
                    courseConfigType={props.courseConfigType}
                />
            )
        }

        return <PassGuaranteeComponent />
    }, [
        props.courseConfigType,
        props.courseName,
        props.coursesInfo,
        props.demoHistory,
        props.isLoggedIn,
        props.setUserInfo,
        props.userPaymentHistory,
    ])

    const selectorText = useMemo((): string => {
        if (props.courseName) {
            const courseInfo = props.coursesInfo.find(
                (courseInfo) => courseInfo.name === props.courseName
            )
            return `${courseInfo.abbreviation}: ${courseInfo.prettyName}`
        }

        return '- Select a course -'
    }, [props.courseName, props.coursesInfo])

    const onClickHandler = useCallback(
        async (courseName: string): Promise<boolean> => {
            logger.info(`onClickHandler: ${courseName}`)
            return await router.replace(
                {
                    pathname: COURSE_NAME_TO_UI_LAYOUT_CONSTANT[courseName],
                },
                undefined,
                { shallow: false }
            )
        },
        [router]
    )

    const courseCardOptions = useMemo((): DefaultOptionType[] => {
        logger.info(`CourseInfo: ${JSON.stringify(props.coursesInfo)}`)
        logger.info(`props: ${JSON.stringify(props)}`)
        // grouped under Health / Life headings in the dropdown
        return groupCoursesByOffering(
            props.coursesInfo
                .filter((course) => course.name !== props.courseName)
                // hiddenButAvailable courses have a page but are not listed
                .filter(
                    (course) =>
                        course.releaseInfo.releaseStatus ===
                        ReleaseStatus.fullyAvailable
                )
        ).map((group) => ({
            label: group.offering,
            options: group.courses.map((course) => ({
                value: course.name,
                label: `${course.abbreviation}: ${course.prettyName}`,
            })),
        }))
    }, [props])

    const courseCardRef = useRef<HTMLDivElement>(null)
    const [courseOptionsContainerWidth, setCourseOptionsContainerWidth] =
        useState<number>(null)
    const setCourseOptionsContainerWidthCallback = useCallback(() => {
        const courseCardWidth = courseCardRef.current?.clientWidth
        if (!courseCardWidth) {
            return false
        }

        if (courseCardWidth < 500) {
            setCourseOptionsContainerWidth(10)
        }

        setCourseOptionsContainerWidth(courseCardWidth)
    }, [])
    useRefResizeEffect(courseCardRef, setCourseOptionsContainerWidthCallback)

    return (
        <div className={styles.courses}>
            <Modal
                title={null}
                centered
                footer={null}
                open={isLoginModalVisible}
                onCancel={() => setIsLoginModalVisible(false)}
                className={authenticatorStyles.authenticatorModal}
            >
                <Authenticator
                    defaultTab={AuthenticatorTabName.signUp}
                    setIsModalVisible={setIsLoginModalVisible}
                    routingPurpose={loginModalRoutingPurpose}
                    courseConfigType={props.courseConfigType}
                />
            </Modal>
            <InstructorModal
                instructor={activeInstructor}
                setActiveInstructor={setActiveInstructor}
            />
            <div className={styles.courseCards}>
                <div
                    className={styles.courseCardOptionsContainer}
                    style={{
                        width: courseOptionsContainerWidth,
                        margin: '0 auto',
                    }}
                >
                    <div
                        className={styles.courseCardOptionsContainerBackground}
                    >
                        <div className={styles.courseCardOptionsTitle}>
                            Available Courses
                        </div>
                        {courseCardOptions && courseCardOptions.length > 0 && (
                            <Select
                                options={courseCardOptions}
                                onChange={onClickHandler}
                                placeholder={
                                    <div className={styles.courseSelector}>
                                        <span
                                            className={
                                                styles.courseSelectorText
                                            }
                                            title={selectorText}
                                        >
                                            {selectorText}
                                        </span>
                                    </div>
                                }
                                popupClassName={
                                    styles.courseOptionsSelectorDropdown
                                }
                            />
                        )}
                    </div>
                </div>
                <div className={styles.courseCardContainer} ref={courseCardRef}>
                    {courseCard}
                </div>
            </div>
        </div>
    )
}
